import type { ImportExecutionCountsDTO, ImportExecutionListItemDTO } from '@today/types'
import type { ImportCountsView, ImportExecutionRowView } from '@today/ui-domain'
import type { TFunction } from 'i18next'

/** Shared presentation helpers of the Admin → Imports pages (TO-399). */

/** Every list asks for 20 rows per page (BR-6). */
export const PAGE_SIZE = '20'

/** `—` for a value that does not exist yet. */
export const DASH = '—'

/** A date-time for display in the active language; `—` when absent. */
export const formatDateTime = (iso: string | null, language: string): string =>
	iso ? new Intl.DateTimeFormat(language, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso)) : DASH

/** `page` + `limit` + each set filter, in the API's `filters[key]` form. */
export const listParams = (page: number, filters: Record<string, string>): Record<string, string> => ({
	page: String(page),
	limit: PAGE_SIZE,
	...Object.fromEntries(
		Object.entries(filters)
			.filter(([, value]) => value !== '')
			.map(([key, value]) => [`filters[${key}]`, value]),
	),
})

/** "All" plus one option per enum value, labelled from `<group>.<value>`. */
export const enumOptions = (t: TFunction, group: string, values: readonly string[]) => [
	{ id: '', label: t('common.all') },
	...values.map((value) => ({ id: value, label: t(`${group}.${value}`) })),
]

const formatCount = (value: number | null, language: string): string =>
	value === null ? DASH : new Intl.NumberFormat(language).format(value)

export const toCountsView = (counts: ImportExecutionCountsDTO, language: string): ImportCountsView => ({
	received: formatCount(counts.received, language),
	valid: formatCount(counts.valid, language),
	rejected: formatCount(counts.rejected, language),
	created: formatCount(counts.created, language),
	updated: formatCount(counts.updated, language),
	unchanged: formatCount(counts.unchanged, language),
	deactivated: formatCount(counts.deactivated, language),
	planRejected: formatCount(counts.planRejected, language),
})

export const toRowView = (
	item: ImportExecutionListItemDTO,
	t: TFunction,
	language: string,
): ImportExecutionRowView => ({
	id: item.id,
	fileName: item.originalFilename,
	logicalFileLabel: t(`logicalFile.${item.logicalFile}`),
	transportLabel: t(`transport.${item.transport}`),
	definition: `${item.importDefinition.key}@${item.importDefinition.version}`,
	receivedAt: formatDateTime(item.receivedAt, language),
	statusLabel: t(`status.${item.status}`),
	counts: toCountsView(item.counts, language),
})

/** Opens a short-lived download URL in a new tab; it is never stored (BR-11). */
export const openDownload = (url: string): void => {
	window.open(url, '_blank', 'noopener')
}
